import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

// Shared wiring for the announcement modals exposed on window. The caller assigns
// the window globals itself so window.d.ts keeps the exact names typed. Emits the
// `<name>:change` CustomEvent the sidebar panels listen to, and closes the modal
// on Escape and on route change.
export default function useWindowModal(
  changeEventName: string,
  assignWindowGlobals: (open: () => void, close: () => void) => void
) {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  useEffect(() => {
    assignWindowGlobals(open, close)
  }, [])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(changeEventName, { detail: { isOpen } }))
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [isOpen])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  return { isOpen, open, close }
}
